import * as polygonClipping from "polygon-clipping";
import apiConfig from "../../../../service/apiConfig";
import L from "leaflet";
import leafletImage from "leaflet-image";

const calculatePolygonArea = (coordinates: [number, number][]) => {
  let area = 0;
  const R = 20925524.9;
  const toRadians = (deg: number) => (deg * Math.PI) / 180;
  for (let i = 0; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i];
    const [lon2, lat2] = coordinates[(i + 1) % coordinates.length];
    area +=
      toRadians(lon2 - lon1) *
      (2 + Math.sin(toRadians(lat1)) + Math.sin(toRadians(lat2)));
  }
  area = (area * R * R) / 2;
  return Math.round(Math.abs(area));
};

function getBBox(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
): [number, number, number, number] {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  feature.geometry.coordinates[0].forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  });
  return [minLng, minLat, maxLng, maxLat];
}

function generateSquareGrid(
  bbox: [number, number, number, number],
  cellSizeMeters: number,
) {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const degPerMeter = 1 / 111320;
  const cellDeg = cellSizeMeters * degPerMeter;
  const cells: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

  for (let lat = minLat; lat < maxLat; lat += cellDeg) {
    for (let lng = minLng; lng < maxLng; lng += cellDeg) {
      cells.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [lng, lat],
              [lng + cellDeg, lat],
              [lng + cellDeg, lat + cellDeg],
              [lng, lat + cellDeg],
              [lng, lat],
            ],
          ],
        },
        properties: {},
      });
    }
  }
  return cells;
}

const convertGeoJSONToPropertyAreas = (featureCollection, deepLawnPolygon) => {
  const minPolygonAreaSqFt = 2000;
  const targetCellAreaSqFt = 2000;
  const sqftToSqM = 0.092903;
  const GARDEN_COLOR = "#FF6B35";

  const transformCoordinates = (coords: [number, number][]) =>
    coords.map((coord) => [coord[1], coord[0]] as [number, number]);

  const createPropertyArea = (
    areaCounter: number,
    featureIndex: number,
    coordinates: [number, number][],
    size: number,
    originalSize: number,
    label: string,
    options: { splitIndex?: number; isOriginal?: boolean } = {},
  ) => {
    const { splitIndex = 0, isOriginal = true } = options;
    return {
      id: `garden_${areaCounter}`,
      label: label,
      coordinates,
      color: GARDEN_COLOR,
      size,
      originalFeatureIndex: featureIndex,
      splitIndex,
      isOriginal,
      originalSize,
    };
  };

  const processAreaFeatures = (
    features: GeoJSON.Feature<GeoJSON.Polygon>[],
    areaType: string,
    areaCounter: number,
  ) => {
    const areas = [];
    let counter = areaCounter;

    for (let featureIndex = 0; featureIndex < features.length; featureIndex++) {
      const feature = features[featureIndex];
      const coordinates = feature.geometry.coordinates[0] as [number, number][];

      const areaSqFt = calculatePolygonArea(coordinates);

      // Determine label based on area type
      const getLabel = (isOriginal: boolean, splitIndex: number) => {
        if (areaType === "Side Yard") {
          return isOriginal ? `Side Yard` : `Side Yard-${splitIndex + 1}`;
        }
        if (areaType === "Back Yard" || areaType === "Front Yard") {
          return isOriginal ? areaType : `${areaType}-${splitIndex + 1}`;
        }
        return isOriginal ? areaType : `${areaType}-${splitIndex + 1}`;
      };

      if (areaSqFt > minPolygonAreaSqFt && !deepLawnPolygon) {
        const cellSizeMeters = Math.sqrt(targetCellAreaSqFt * sqftToSqM);
        const bbox = getBBox(feature);
        const grid = generateSquareGrid(bbox, cellSizeMeters);
        let anySplit = false;

        grid.forEach((cell, i) => {
          const intersection = polygonClipping.intersection(
            feature.geometry.coordinates as polygonClipping.Geom,
            cell.geometry.coordinates as polygonClipping.Geom,
          );
          if (intersection && intersection.length > 0) {
            anySplit = true;
            const clipped: GeoJSON.Feature<GeoJSON.Polygon> = {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: intersection[0] as number[][][],
              },
              properties: {},
            };
            const areaSqFtSub = calculatePolygonArea(
              clipped.geometry.coordinates[0] as [number, number][],
            );
            const transformedCoords = transformCoordinates(
              clipped.geometry.coordinates[0] as [number, number][],
            );

            counter++;
            areas.push(
              createPropertyArea(
                counter,
                featureIndex,
                transformedCoords,
                areaSqFtSub,
                areaSqFt,
                getLabel(false, i),
                { splitIndex: i, isOriginal: false },
              ),
            );
          }
        });

        if (!anySplit) {
          const transformedCoords = transformCoordinates(
            coordinates as [number, number][],
          );
          counter++;
          areas.push(
            createPropertyArea(
              counter,
              featureIndex,
              transformedCoords,
              areaSqFt,
              areaSqFt,
              getLabel(true, 0),
            ),
          );
        }
      } else {
        const transformedCoords = transformCoordinates(
          coordinates as [number, number][],
        );
        counter++;
        areas.push(
          createPropertyArea(
            counter,
            featureIndex,
            transformedCoords,
            areaSqFt,
            areaSqFt,
            getLabel(true, 0),
          ),
        );
      }
    }

    return { areas, counter };
  };

  const propertyAreas = [];
  let areaCounter = 0;

  // Process BACK_YARD
  const backYardFeatures = featureCollection?.BACK_YARD?.features;
  if (backYardFeatures) {
    const result = processAreaFeatures(
      backYardFeatures,
      "Back Yard",
      areaCounter,
    );
    propertyAreas.push(...result.areas);
    areaCounter = result.counter;
  }

  // Process FRONT_YARD
  const frontYardFeatures = featureCollection?.FRONT_YARD?.features;
  if (frontYardFeatures) {
    const result = processAreaFeatures(
      frontYardFeatures,
      "Front Yard",
      areaCounter,
    );
    propertyAreas.push(...result.areas);
    areaCounter = result.counter;
  }
  return propertyAreas;
};

const geocodeAddress = async (
  address: string,
): Promise<[number, number] | null> => {
  const url = `${apiConfig.geoLocationURL}?q=${encodeURIComponent(address)}&apiKey=${apiConfig.DISCOVER_API_KEY}`;

  try {
    const data = await globalThis.GetCachedUnifiedSync(url);
    const position = data?.items?.[0]?.position;
    if (position?.lat != null && position?.lng != null) {
      const { lat, lng } = position;
      return [lat, lng];
    }
    return null;
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
};

const capturePolygonImage = (
  map: L.Map,
  callback: (imageUrl: string | null) => void,
): void => {
  if (!map) {
    console.error("Map instance is required");
    callback(null);
    return;
  }

  try {
    const polygonData: Array<{
      polygon: L.Polygon;
      color: string;
      fillColor: string;
      fillOpacity: number;
      weight: number;
    }> = [];

    const markerData: Array<{
      marker: L.Marker;
      label: string;
      position: L.LatLng;
    }> = [];

    map.eachLayer((layer) => {
      if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
        const polygon = layer;
        const options = polygon.options;
        polygonData.push({
          polygon: polygon,
          color: options.color || "#ff7800",
          fillColor: options.fillColor || "#ff7800",
          fillOpacity: options.fillOpacity || 0.3,
          weight: options.weight || 2,
        });
      } else if (layer instanceof L.Marker) {
        const marker = layer;
        const icon = marker.options.icon;
        if (icon && icon.options?.className === "polygon-label") {
          const iconHtml = (icon as any).options?.html || "";
          const labelMatch = iconHtml.match(/<div>(.*?)<\/div>/);
          const label = labelMatch ? labelMatch[1] : "";
          markerData.push({
            marker: marker,
            label: label,
            position: marker.getLatLng(),
          });
          map.removeLayer(marker);
        }
      }
    });

    leafletImage(map, (err: Error | null, canvas: HTMLCanvasElement) => {
      markerData.forEach(({ marker }) => {
        if (map) {
          marker.addTo(map);
        }
      });

      if (err) {
        console.error("Error capturing map image:", err);
        callback(null);
        return;
      }

      if (!canvas) {
        console.error("Canvas not generated");
        callback(null);
        return;
      }

      try {
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          callback(null);
          return;
        }

        const mapSize = map.getSize();
        const scale = canvas.width / mapSize.x;
        polygonData.forEach(
          ({ polygon, color, fillColor, fillOpacity, weight }) => {
            const latLngs = polygon.getLatLngs()[0] as L.LatLng[];

            if (latLngs && latLngs.length > 0) {
              ctx.beginPath();

              latLngs.forEach((latLng, index) => {
                const point = map.latLngToContainerPoint(latLng);
                const x = point.x * scale;
                const y = point.y * scale;

                if (index === 0) {
                  ctx.moveTo(x, y);
                } else {
                  ctx.lineTo(x, y);
                }
              });

              ctx.closePath();

              // Fill polygon
              ctx.fillStyle = fillColor;
              ctx.globalAlpha = fillOpacity;
              ctx.fill();

              // Stroke polygon border
              ctx.globalAlpha = 1;
              ctx.strokeStyle = color;
              ctx.lineWidth = weight * scale;
              ctx.stroke();
            }
          },
        );

        // Reset alpha
        ctx.globalAlpha = 1;

        // Draw labels on the canvas
        if (markerData.length > 0) {
          ctx.font = `bold ${Math.floor(14 * scale)}px Arial`;
          ctx.fillStyle = "white";
          ctx.strokeStyle = "black";
          ctx.lineWidth = 3 * scale;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          markerData.forEach(({ label, position }) => {
            const point = map.latLngToContainerPoint(position);
            const x = point.x * scale;
            const y = point.y * scale;

            // Draw text outline
            ctx.strokeText(label, x, y);
            // Draw text fill
            ctx.fillText(label, x, y);
          });
        }

        // Convert canvas to blob for binary upload
        canvas.toBlob((blob) => {
          if (blob) {
            // Store blob directly for S3 upload
            globalThis.selectedPolygonImageBlob = blob;
            // Convert blob string into a File object
            globalThis.selectedPolygonImageFile = new File(
              [blob],
              "polygon.png",
              { type: "image/png" },
            );
            const imageUrl = URL.createObjectURL(blob);
            callback(imageUrl);
          } else {
            console.error("Failed to convert canvas to blob");
            callback(null);
          }
        }, "image/png");
      } catch (error) {
        console.error("Error converting canvas to data URL:", error);
        callback(null);
      }
    });
  } catch (error) {
    console.error("Error in capturePolygonImage:", error);
    callback(null);
  }
};

export { convertGeoJSONToPropertyAreas, geocodeAddress, capturePolygonImage };
